import { spawn } from "node:child_process";
import { stat } from "node:fs/promises";
import { join } from "node:path";
import { cancel, confirm, isCancel } from "@clack/prompts";
import packageJson from "../../package.json";
import {
  buildNextVersionState,
  fetchLatestPackageInfo,
  readVersionState,
  writeVersionState,
} from "../config/version-state";
import { runDaemonServiceFullAlignment } from "../local-service/lifecycle/daemon-service-alignment-executor";
import {
  buildDaemonServiceTargetSpec,
  resolveDaemonServiceAlignmentDecision,
} from "../local-service/lifecycle/daemon-service-alignment-target";
import {
  type DaemonServiceFailureStage,
  formatDaemonFailureContextText,
} from "../local-service/lifecycle/daemon-service-evidence";
import { inspectDaemonServiceInventory } from "../local-service/lifecycle/daemon-service-inventory";
import { resolveVersionChangeDirection } from "../local-service/lifecycle/version-change-direction";
import {
  formatInlineWakeLoopCliCommands,
  formatWakeLoopCliTextBlock,
} from "../runtime/cli-entry-command";
import { resolveRuntimeContext } from "../runtime/context";
import {
  type NpmCommandSpec,
  resolveCurrentNpmCommandSpec,
  resolveCurrentNpmGlobalPackageInstall,
} from "../runtime/current-npm-global-install";
import { createUpgradeConfirmationGuideError } from "../runtime/dual-view-guide-catalog";
import { canUseInteractivePrompts } from "../runtime/interaction";
import { buildManualWakeLoopUpgradeCommand } from "../runtime/manual-upgrade";
import {
  diagnoseNpmPackageAcquisitionFailure,
  formatNpmPackageAcquisitionFailureDiagnosis,
} from "../runtime/npm-package-acquisition-diagnostics";
import { createPresenter } from "../runtime/presenter/index";
import { createDaemonServiceAlignmentProgressUi } from "../system/local-service-alignment-progress-ui";
import {
  formatDaemonServiceDowngradeBlockedRecommendation,
  formatDaemonServiceMissingRecommendation,
} from "../system/local-service-install-attention";
import { emitTopLevelAgentOverview } from "./top-level-agent-overview";

const WAKELOOP_PACKAGE_NAME = "wakeloop";
export const UPGRADE_INTERNAL_ALIGN_SERVICE_FLAG = "--internal-align-service";

class UpgradeOutputHandledError extends Error {
  readonly code = "wakeloop.upgrade.output_handled";

  constructor(message: string) {
    super(message);
    this.name = "UpgradeOutputHandledError";
  }
}

export function isUpgradeOutputHandledError(
  error: unknown
): error is UpgradeOutputHandledError {
  return (
    error instanceof UpgradeOutputHandledError ||
    (typeof error === "object" &&
      error !== null &&
      "code" in error &&
      (error as { code?: unknown }).code === "wakeloop.upgrade.output_handled")
  );
}

type UpgradeStatus =
  | {
      status: "up_to_date" | "outdated" | "downgrade_blocked";
      currentVersion: string;
      latestVersion: string;
      requiredFloor: string | null;
      checkedAt: string;
    }
  | {
      status: "check_failed";
      currentVersion: string;
      checkedAt: string;
      error: string;
    };

export async function runUpgrade(input: {
  view?: string;
  yes?: boolean;
  check?: boolean;
  internalAlignService?: boolean;
}): Promise<void> {
  const runtime = await resolveRuntimeContext({
    view: input.view,
  });
  const presenter = createPresenter(runtime);
  if (runtime.resolvedView === "agent") {
    emitTopLevelAgentOverview({
      presenter,
      command: "upgrade",
    });
  }

  if (input.internalAlignService) {
    await runInternalUpgradeServiceAlignment({
      presenter,
      resolvedView: runtime.resolvedView,
      daemonVersion: packageJson.version,
      ...(process.argv[1] ? { cliScriptPath: process.argv[1] } : {}),
    });
    return;
  }

  const allowPrompt = canUseInteractivePrompts(runtime);
  const currentVersion = packageJson.version;
  const { currentGlobalInstall, manualUpgradeCommand } =
    await resolveUpgradeInstallContext();
  const previousVersionState = await readVersionState();
  if (!previousVersionState) {
    handleMissingVersionMetadata({
      presenter,
      resolvedView: runtime.resolvedView,
      manualUpgradeCommand,
    });
  }

  const checkedAt = new Date().toISOString();
  const upgradeStatus = await resolveUpgradeStatus({
    currentVersion,
    checkedAt,
  });
  if (upgradeStatus.status !== "check_failed") {
    await writeVersionState(
      buildNextVersionState({
        previous: previousVersionState,
        currentVersion,
        latestVersion: upgradeStatus.latestVersion,
        requiredFloor: upgradeStatus.requiredFloor,
        checkedAt,
      })
    );
  }

  if (input.check) {
    presenter.data({
      code: "upgrade.check",
      payload: upgradeStatus,
    });
    return;
  }

  if (upgradeStatus.status === "up_to_date") {
    if (runtime.resolvedView === "agent") {
      presenter.data({
        code: "upgrade.up_to_date",
        payload: upgradeStatus,
      });
    } else {
      presenter.line({
        code: "upgrade.up_to_date",
        text: `WakeLoop CLI is already up to date (${upgradeStatus.currentVersion}).`,
      });
    }
    return;
  }

  if (upgradeStatus.status === "downgrade_blocked") {
    if (runtime.resolvedView === "agent") {
      presenter.data({
        code: "upgrade.downgrade_blocked",
        payload: upgradeStatus,
      });
    } else {
      presenter.line({
        code: "upgrade.downgrade_blocked",
        text: `WakeLoop CLI is already newer than npm latest (current v${upgradeStatus.currentVersion}, npm latest v${upgradeStatus.latestVersion}). Downgrade is blocked.`,
      });
    }
    return;
  }

  if (upgradeStatus.status === "outdated") {
    const shouldRun = await resolveUpgradeConfirmation({
      allowPrompt,
      yes: Boolean(input.yes),
      message: `Upgrade WakeLoop CLI from ${upgradeStatus.currentVersion} to ${upgradeStatus.latestVersion}?`,
      nonInteractiveError:
        "wakeloop upgrade requires --yes in non-interactive mode when upgrade is available",
      resolvedView: runtime.resolvedView,
      scenario: "outdated",
    });
    if (!shouldRun) {
      return;
    }
  }

  if (upgradeStatus.status === "check_failed") {
    handleUpgradeCheckFailure({
      presenter,
      resolvedView: runtime.resolvedView,
      upgradeStatus,
      manualUpgradeCommand,
    });
  }

  emitUpgradeInstalling({
    presenter,
    resolvedView: runtime.resolvedView,
    manualUpgradeCommand,
  });
  const npmCommandSpec = await resolveCurrentNpmCommandSpec();
  await installLatestPackage({
    npmCommandSpec,
    currentGlobalInstall,
    manualUpgradeCommand,
  });

  const latestVersion = upgradeStatus.latestVersion;
  await writeVersionState(
    buildNextVersionState({
      previous: previousVersionState,
      currentVersion: latestVersion,
      latestVersion,
      requiredFloor: upgradeStatus.requiredFloor,
      checkedAt: new Date().toISOString(),
    })
  );

  await handoffUpgradeServiceAlignmentToLatestBinary({
    resolvedView: runtime.resolvedView,
    cliScriptPath:
      currentGlobalInstall?.cliScriptPath ??
      (await resolveLatestInstalledCliScriptPath({
        npmCommandSpec,
      })),
  });

  emitUpgradeCompleted({
    presenter,
    resolvedView: runtime.resolvedView,
    previousVersion: currentVersion,
    currentVersion: latestVersion,
  });
}

async function resolveUpgradeInstallContext(): Promise<{
  currentGlobalInstall: Awaited<
    ReturnType<typeof resolveCurrentNpmGlobalPackageInstall>
  >;
  manualUpgradeCommand: string;
}> {
  const currentGlobalInstall = await resolveCurrentNpmGlobalPackageInstall({
    packageName: WAKELOOP_PACKAGE_NAME,
  });
  return {
    currentGlobalInstall,
    manualUpgradeCommand: buildManualWakeLoopUpgradeCommand({
      globalPrefixPath: currentGlobalInstall?.globalPrefixPath ?? null,
    }),
  };
}

function handleMissingVersionMetadata(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: "human" | "agent";
  manualUpgradeCommand: string;
}): never {
  const errorMessage = formatWakeLoopCliTextBlock(
    `WakeLoop version metadata is missing, so automatic upgrade cannot continue.\nManual fallback: \`${input.manualUpgradeCommand}\`\nIf you want to repair local WakeLoop state first, run \`wakeloop reset --yes\` before \`wakeloop upgrade\`.`
  );
  if (input.resolvedView === "agent") {
    input.presenter.data({
      code: "upgrade.metadata.missing",
      payload: {
        summary:
          "Local WakeLoop version metadata is missing, so automatic upgrade cannot verify downgrade safety.",
        nextSteps: [
          input.manualUpgradeCommand,
          "wakeloop-dev reset --yes --view agent",
          "wakeloop-dev start",
          "wakeloop-dev upgrade --check --view agent",
        ],
        humanActionRequired: false,
      },
    });
    throw new UpgradeOutputHandledError(errorMessage);
  }
  throw new Error(errorMessage);
}

function handleUpgradeCheckFailure(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: "human" | "agent";
  upgradeStatus: Extract<UpgradeStatus, { status: "check_failed" }>;
  manualUpgradeCommand: string;
}): never {
  const errorMessage = formatWakeLoopCliTextBlock(
    `Version check failed (${input.upgradeStatus.error}). Upgrade stopped because downgrade protection requires confirming that npm latest is newer than the current CLI.\nManual fallback: \`${input.manualUpgradeCommand}\``
  );
  if (input.resolvedView === "agent") {
    input.presenter.data({
      code: "upgrade.check_failed",
      payload: {
        ...input.upgradeStatus,
        nextSteps: [
          input.manualUpgradeCommand,
          "wakeloop-dev upgrade --check --view agent",
          "wakeloop-dev upgrade --yes --view agent",
        ],
        humanActionRequired: false,
      },
    });
    throw new UpgradeOutputHandledError(errorMessage);
  }
  throw new Error(errorMessage);
}

async function resolveUpgradeStatus(input: {
  currentVersion: string;
  checkedAt: string;
}): Promise<UpgradeStatus> {
  try {
    const latestInfo = await fetchLatestPackageInfo(WAKELOOP_PACKAGE_NAME);
    const latestVersion = latestInfo.version;
    const direction = resolveVersionChangeDirection({
      fromVersion: input.currentVersion,
      toVersion: latestVersion,
    });
    const status = resolveUpgradeStatusKind(direction);
    return {
      status,
      currentVersion: input.currentVersion,
      latestVersion,
      requiredFloor: latestInfo.minRequiredCliVersion,
      checkedAt: input.checkedAt,
    };
  } catch (error) {
    return {
      status: "check_failed",
      currentVersion: input.currentVersion,
      checkedAt: input.checkedAt,
      error: toErrorMessage(error),
    };
  }
}

function resolveUpgradeStatusKind(
  direction: ReturnType<typeof resolveVersionChangeDirection>
): "up_to_date" | "outdated" | "downgrade_blocked" {
  if (direction === "upgrade") {
    return "outdated";
  }
  if (direction === "downgrade") {
    return "downgrade_blocked";
  }
  return "up_to_date";
}

async function resolveUpgradeConfirmation(input: {
  allowPrompt: boolean;
  yes: boolean;
  message: string;
  nonInteractiveError: string;
  resolvedView: "human" | "agent";
  scenario: "outdated" | "check_failed";
}): Promise<boolean> {
  if (input.yes) {
    return true;
  }
  if (!input.allowPrompt) {
    if (input.resolvedView === "agent") {
      throw createUpgradeConfirmationGuideError(input.scenario);
    }
    throw new Error(
      formatWakeLoopCliTextBlock(
        `${input.nonInteractiveError}\nexample: \`wakeloop upgrade --yes\``
      )
    );
  }

  const choice = await confirm({
    message: input.message,
    initialValue: true,
  });
  if (isCancel(choice) || !choice) {
    cancel("Cancelled.");
    return false;
  }
  return true;
}

async function installLatestPackage(input: {
  npmCommandSpec: NpmCommandSpec;
  currentGlobalInstall: Awaited<
    ReturnType<typeof resolveCurrentNpmGlobalPackageInstall>
  >;
  manualUpgradeCommand: string;
}): Promise<void> {
  const args = input.currentGlobalInstall
    ? [
        "install",
        "-g",
        "--prefix",
        input.currentGlobalInstall.globalPrefixPath,
        `${WAKELOOP_PACKAGE_NAME}@latest`,
      ]
    : ["install", "-g", `${WAKELOOP_PACKAGE_NAME}@latest`];
  const result = await runCommandSpec(input.npmCommandSpec, args);

  if (result.code !== 0) {
    const stderr = result.stderr.trim();
    const stdout = result.stdout.trim();
    const details = stderr || stdout;
    const diagnosis = diagnoseNpmPackageAcquisitionFailure({
      packageName: WAKELOOP_PACKAGE_NAME,
      text: [stderr, stdout].filter(Boolean).join("\n"),
    });
    const diagnosisText = diagnosis
      ? `\n${formatNpmPackageAcquisitionFailureDiagnosis(diagnosis)}`
      : "";
    throw new Error(
      formatWakeLoopCliTextBlock(
        `upgrade failed (exit ${result.code})${details ? `: ${details}` : ""}${diagnosisText}\nManual fallback: \`${input.manualUpgradeCommand}\``
      )
    );
  }
}

async function runInternalUpgradeServiceAlignment(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: "human" | "agent";
  daemonVersion: string;
  cliScriptPath?: string;
}): Promise<void> {
  const inventory = await inspectDaemonServiceInventory({
    expectedVersion: input.daemonVersion,
    intendedMode: "background",
    intent: "upgrade_reconcile",
  });
  const target = buildDaemonServiceTargetSpec({
    daemonVersion: input.daemonVersion,
  });
  const decision = resolveDaemonServiceAlignmentDecision({
    inventory,
    target,
  });

  if (decision.status === "already_aligned") {
    emitUpgradeReconcileAlreadyCurrent({
      presenter: input.presenter,
      resolvedView: input.resolvedView,
      expectedVersion: input.daemonVersion,
    });
    return;
  }

  if (decision.status === "not_installed") {
    emitUpgradeReconcileSkipped({
      presenter: input.presenter,
      resolvedView: input.resolvedView,
      reason: "not_installed",
    });
    return;
  }

  if (decision.status === "blocked") {
    const errorMessage = buildUpgradeAlignmentBlockedMessage(
      decision.reasonCodes
    );
    emitUpgradeReconcileFailure({
      presenter: input.presenter,
      resolvedView: input.resolvedView,
      message: errorMessage,
      errorMessage,
      backupDataPath: null,
      failureStage: null,
      evidencePath: null,
    });
    throw new UpgradeOutputHandledError(errorMessage);
  }

  const alignmentProgressUi = createDaemonServiceAlignmentProgressUi({
    view: input.resolvedView,
  });
  const alignmentResult = await runDaemonServiceFullAlignment({
    expectedVersion: input.daemonVersion,
    intent: "upgrade_reconcile",
    ...(input.cliScriptPath ? { cliScriptPath: input.cliScriptPath } : {}),
    onProgress: alignmentProgressUi.onProgress,
  });

  if (alignmentResult.status === "aligned") {
    alignmentProgressUi.complete();
    emitUpgradeReconcileCompleted({
      presenter: input.presenter,
      resolvedView: input.resolvedView,
      expectedVersion: input.daemonVersion,
    });
    return;
  }

  if (alignmentResult.status === "stopped_safe") {
    alignmentProgressUi.fail();
    emitUpgradeReconcileStoppedSafe({
      presenter: input.presenter,
      resolvedView: input.resolvedView,
      expectedVersion: input.daemonVersion,
      errorMessage: alignmentResult.errorMessage,
      failureStage: alignmentResult.failureStage ?? null,
      evidencePath: alignmentResult.evidencePath ?? null,
    });
    return;
  }

  if (alignmentResult.status === "already_aligned") {
    alignmentProgressUi.complete();
    emitUpgradeReconcileAlreadyCurrent({
      presenter: input.presenter,
      resolvedView: input.resolvedView,
      expectedVersion: input.daemonVersion,
    });
    return;
  }

  alignmentProgressUi.fail();

  const errorMessage =
    alignmentResult.status === "blocked"
      ? buildUpgradeAlignmentBlockedMessage(alignmentResult.reasonCodes)
      : alignmentResult.errorMessage;
  emitUpgradeReconcileFailure({
    presenter: input.presenter,
    resolvedView: input.resolvedView,
    message:
      alignmentResult.status === "blocked"
        ? errorMessage
        : formatUpgradeReconcileFailureMessage({
            errorMessage,
            backupDataPath: alignmentResult.backupDataPath,
            failureStage: alignmentResult.failureStage ?? null,
            evidencePath: alignmentResult.evidencePath ?? null,
          }),
    errorMessage,
    backupDataPath:
      alignmentResult.status === "failed"
        ? alignmentResult.backupDataPath
        : null,
    failureStage:
      alignmentResult.status === "failed"
        ? (alignmentResult.failureStage ?? null)
        : null,
    evidencePath:
      alignmentResult.status === "failed"
        ? (alignmentResult.evidencePath ?? null)
        : null,
  });
  throw new UpgradeOutputHandledError(errorMessage);
}

async function handoffUpgradeServiceAlignmentToLatestBinary(input: {
  resolvedView: "human" | "agent";
  cliScriptPath: string;
}): Promise<void> {
  const useInheritedOutput =
    input.resolvedView === "human" &&
    process.stdout.isTTY === true &&
    process.stderr.isTTY === true;
  const handoffResult = await runStreamingCommand(
    process.execPath,
    [
      input.cliScriptPath,
      "--view",
      input.resolvedView,
      "upgrade",
      UPGRADE_INTERNAL_ALIGN_SERVICE_FLAG,
    ],
    {
      WAKELOOP_INTERNAL_SKIP_STARTUP_UPGRADE: "1",
    },
    useInheritedOutput
  );
  if (handoffResult.code !== 0) {
    if (useInheritedOutput) {
      throw new Error(
        formatUpgradeReconcileFailureMessage({
          errorMessage: `latest CLI handoff exited with status ${String(
            handoffResult.code
          )}`,
          backupDataPath: null,
          failureStage: null,
          evidencePath: null,
        })
      );
    }
    const hasForwardedOutput =
      handoffResult.stderr.trim().length > 0 ||
      handoffResult.stdout.trim().length > 0;
    if (hasForwardedOutput) {
      throw new UpgradeOutputHandledError(
        "Latest WakeLoop CLI handoff reported a service alignment failure."
      );
    }
    throw new Error(
      formatUpgradeReconcileFailureMessage({
        errorMessage: `latest CLI handoff exited with status ${String(handoffResult.code)}`,
        backupDataPath: null,
        failureStage: null,
        evidencePath: null,
      })
    );
  }
}

async function resolveLatestInstalledCliScriptPath(input: {
  npmCommandSpec: NpmCommandSpec;
}): Promise<string> {
  const npmRootResult = await runCommandSpec(input.npmCommandSpec, [
    "root",
    "-g",
  ]);
  if (npmRootResult.code !== 0) {
    throw new Error(
      npmRootResult.stderr.trim() ||
        npmRootResult.stdout.trim() ||
        "npm root -g failed"
    );
  }
  const cliScriptPath = join(
    npmRootResult.stdout.trim(),
    WAKELOOP_PACKAGE_NAME,
    "dist",
    "wakeloop.js"
  );
  await stat(cliScriptPath);
  return cliScriptPath;
}

async function runCommand(
  command: string,
  args: string[]
): Promise<{
  code: number;
  stdout: string;
  stderr: string;
}> {
  return await runSpawnedCommand({
    command,
    args,
    forwardOutput: false,
  });
}

async function runCommandSpec(
  commandSpec: NpmCommandSpec,
  args: string[]
): Promise<{
  code: number;
  stdout: string;
  stderr: string;
}> {
  return await runCommand(commandSpec.command, [
    ...commandSpec.argsPrefix,
    ...args,
  ]);
}

async function runStreamingCommand(
  command: string,
  args: string[],
  envOverrides: NodeJS.ProcessEnv = {},
  useInheritedOutput = false
): Promise<{
  code: number;
  stdout: string;
  stderr: string;
}> {
  return await runSpawnedCommand({
    command,
    args,
    forwardOutput: true,
    envOverrides,
    useInheritedOutput,
  });
}

async function runSpawnedCommand(input: {
  command: string;
  args: string[];
  forwardOutput: boolean;
  envOverrides?: NodeJS.ProcessEnv;
  useInheritedOutput?: boolean;
}): Promise<{
  code: number;
  stdout: string;
  stderr: string;
}> {
  return await new Promise((resolve, reject) => {
    const child = spawn(input.command, input.args, {
      stdio:
        input.forwardOutput && input.useInheritedOutput
          ? ["ignore", "inherit", "inherit"]
          : ["ignore", "pipe", "pipe"],
      env: {
        ...process.env,
        ...(input.envOverrides ?? {}),
      },
    });
    let stdout = "";
    let stderr = "";

    if (child.stdout) {
      child.stdout.on("data", (chunk: Buffer) => {
        const text = chunk.toString();
        stdout += text;
        if (input.forwardOutput) {
          process.stdout.write(text);
        }
      });
    }
    if (child.stderr) {
      child.stderr.on("data", (chunk: Buffer) => {
        const text = chunk.toString();
        stderr += text;
        if (input.forwardOutput) {
          process.stderr.write(text);
        }
      });
    }
    child.on("error", (error) => {
      reject(error);
    });
    child.on("close", (code) => {
      resolve({
        code: Number(code ?? 1),
        stdout,
        stderr,
      });
    });
  });
}

function formatUpgradeReconcileFailureMessage(input: {
  errorMessage: string;
  backupDataPath: string | null;
  failureStage?: DaemonServiceFailureStage | null;
  evidencePath: string | null;
}): string {
  const failureContext = formatDaemonFailureContextText({
    backupDataPath: input.backupDataPath,
    evidencePath: input.evidencePath,
    failureStage: input.failureStage ?? null,
  });
  return formatInlineWakeLoopCliCommands(
    `Service reconcile failed: ${input.errorMessage}. Please run \`wakeloop service repair\` manually.${
      failureContext.length > 0 ? ` ${failureContext}` : ""
    }`
  );
}

function buildUpgradeAlignmentBlockedMessage(
  reasonCodes: readonly string[]
): string {
  if (reasonCodes.includes("downgrade_blocked")) {
    return formatDaemonServiceDowngradeBlockedRecommendation({
      expectedVersion: packageJson.version,
    });
  }
  const reasonText =
    reasonCodes.length > 0 ? reasonCodes.join(", ") : "unknown";
  return formatInlineWakeLoopCliCommands(
    `Service alignment is blocked (${reasonText}). Please run \`wakeloop service status\` or \`wakeloop service repair\` manually.`
  );
}

function emitUpgradeReconcileCompleted(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: "human" | "agent";
  expectedVersion: string;
}): void {
  input.presenter.line({
    code: "upgrade.reconcile.completed",
    text: `WakeLoop Service reconciled to v${input.expectedVersion}.`,
  });
}

function emitUpgradeReconcileAlreadyCurrent(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: "human" | "agent";
  expectedVersion: string;
}): void {
  if (input.resolvedView === "human") {
    input.presenter.line({
      code: "upgrade.reconcile.already_current",
      text: `WakeLoop Service is already current (v${input.expectedVersion}).`,
    });
    return;
  }

  input.presenter.data({
    code: "upgrade.reconcile.already_current",
    payload: {
      status: "already_current",
      expectedVersion: input.expectedVersion,
    },
  });
}

function emitUpgradeReconcileFailure(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: "human" | "agent";
  message: string;
  errorMessage: string;
  backupDataPath: string | null;
  failureStage?: string | null;
  evidencePath: string | null;
}): void {
  if (input.resolvedView === "human") {
    input.presenter.line({
      code: "upgrade.reconcile.failed",
      text: input.message,
    });
    return;
  }

  input.presenter.data({
    code: "upgrade.reconcile.failed",
    payload: {
      ok: false,
      message: input.message,
      errorMessage: input.errorMessage,
      ...(input.backupDataPath ? { backupDataPath: input.backupDataPath } : {}),
      ...(input.failureStage ? { failureStage: input.failureStage } : {}),
      ...(input.evidencePath ? { evidencePath: input.evidencePath } : {}),
      command: "wakeloop service repair",
    },
  });
}

function emitUpgradeReconcileStoppedSafe(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: "human" | "agent";
  expectedVersion: string;
  errorMessage: string;
  failureStage?:
    | import("../local-service/lifecycle/daemon-service-evidence").DaemonServiceFailureStage
    | null;
  evidencePath: string | null;
}): void {
  const message = formatInlineWakeLoopCliCommands(
    `WakeLoop Service reconciled to v${input.expectedVersion} and is safely stopped on the current version. WakeLoop did not roll back to older local service state. Run \`wakeloop service start\` when you want to start it again.${
      input.errorMessage.length > 0 ? ` Reason: ${input.errorMessage}.` : ""
    }${
      input.failureStage || input.evidencePath
        ? ` ${formatDaemonFailureContextText({
            failureStage: input.failureStage ?? null,
            evidencePath: input.evidencePath,
          })}`
        : ""
    }`
  );
  if (input.resolvedView === "human") {
    input.presenter.line({
      code: "upgrade.reconcile.stopped_safe",
      text: message,
    });
    return;
  }
  input.presenter.data({
    code: "upgrade.reconcile.stopped_safe",
    payload: {
      status: "stopped_safe",
      expectedVersion: input.expectedVersion,
      message,
      errorMessage: input.errorMessage,
      ...(input.failureStage ? { failureStage: input.failureStage } : {}),
      ...(input.evidencePath ? { evidencePath: input.evidencePath } : {}),
      command: "wakeloop service start",
    },
  });
}

function resolveUpgradeReconcileSkipMessage(reason: string): {
  text: string;
  command: string;
} {
  if (reason === "not_installed") {
    return {
      text: formatDaemonServiceMissingRecommendation(),
      command: "wakeloop service install",
    };
  }
  if (reason === "downgrade_blocked") {
    return {
      text: formatDaemonServiceDowngradeBlockedRecommendation({
        expectedVersion: packageJson.version,
      }),
      command: "wakeloop upgrade",
    };
  }
  return {
    text: formatInlineWakeLoopCliCommands(
      "Skipped service reconcile. You can run `wakeloop service update` later."
    ),
    command: "wakeloop service update",
  };
}

function emitUpgradeReconcileSkipped(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: "human" | "agent";
  reason: string;
}): void {
  const { text, command } = resolveUpgradeReconcileSkipMessage(input.reason);
  if (input.resolvedView === "human") {
    input.presenter.line({
      code: "upgrade.reconcile.skipped",
      text,
    });
    return;
  }

  input.presenter.data({
    code: "upgrade.reconcile.skipped",
    payload: {
      skipped: true,
      reason: input.reason,
      command,
    },
  });
}

function emitUpgradeInstalling(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: "human" | "agent";
  manualUpgradeCommand: string;
}): void {
  if (input.resolvedView === "agent") {
    input.presenter.data({
      code: "upgrade.installing",
      payload: {
        command: input.manualUpgradeCommand,
      },
    });
    return;
  }
  input.presenter.line({
    code: "upgrade.installing",
    text: "Installing latest WakeLoop CLI with npm...",
  });
}

function emitUpgradeCompleted(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: "human" | "agent";
  previousVersion: string;
  currentVersion: string;
}): void {
  if (input.resolvedView === "agent") {
    input.presenter.data({
      code: "upgrade.completed",
      payload: {
        previousVersion: input.previousVersion,
        currentVersion: input.currentVersion,
      },
    });
    return;
  }
  input.presenter.line({
    code: "upgrade.completed",
    text: "WakeLoop CLI upgrade completed.",
  });
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}
